/**
 * «Мои предложения» — offer'ы фирмы из адреса на грузы, блок `WEB-2.2`,
 * требование 405 (и половиной 404: сюда ведёт поданный offer).
 *
 * Экран читает `listCompanyCargoOffers` фирмы из адреса и показывает **каждый**
 * offer в порядке ответа, истёкшие, отклонённые и вытесненные тоже: маршрут,
 * дата погрузки, груз и кузов из сводки груза, число машин, цена суммой и
 * валютой ответа (CR-003 §1) и подпись согласия или своей цены (`counter`),
 * статус подписью (значение вне перечня — «неизвестно», CR-007), срок ответа
 * логиста, срок подтверждения, когда он есть, и имя подавшего.
 *
 * «Подтвердить» стоит у одобренного offer'а, «Отозвать» — у ждущего и у
 * одобренного (проза операции отзыва: «PENDING или APPROVED → WITHDRAWN»), и обе
 * — только при `can_take_cargo` членства фирмы из адреса (решение формы Б11).
 * Значения двух статусов названы в файле по одному разу, остальные — подписью.
 * Истёк ли срок, клиент по часам не судит: кнопка стоит, а `409` с текстом
 * отвечает сервер. Ответ любого действия — список перечитывается у сервера, а
 * не дописывается клиентом; после подтверждения — ссылка «Ждут назначения»:
 * работы рождены сервером, и назначать их диспетчеру там.
 *
 * Признак членства — из профиля (`getCurrentUser`), читаемого при загрузке: его
 * считает сервер на каждый ответ, кэша у клиента нет. Состояние встаёт по
 * единственному правилу места (долг `TD-66`): ответа нет — на месте экрана,
 * чужая фирма даёт одно «нет доступа» на `403` и `404`; список пуст — на месте
 * списка, заголовок виден.
 */

import { ApiFailure } from '../api/client';
import type { ApiClient } from '../api/client';
import { membershipsIn, nextPageCursor } from '../api/contract';
import type { CargoOffer, Membership } from '../api/contract';
import { activeLocale, bodyTypeStringKey, cargoOfferStatusStringKey } from '../i18n';
import type { Strings } from '../i18n';
import { worksToAssignPath } from '../router';
import { dateLabel, enumLabel, momentTimeLabel, moneyLabel, placeState, refusalText, screenStateFor } from './state';
import type { ScreenState } from './state';

export interface CargoOffersScreenOptions {
  readonly client: ApiClient;
  readonly strings: Strings;
  readonly companyId: string;
}

export interface CargoOffersScreen {
  readonly element: HTMLElement;
  /** Состояние экрана либо `null` — содержимое показано. */
  readonly state: ScreenState | null;
  load(): Promise<void>;
}

/**
 * Одобренный offer — его подтверждает перевозчик. Значение названо в файле
 * один раз: оно решает не «можно ли», а «предлагать ли кнопку» — срок и право
 * всё равно судит сервер.
 */
const CONFIRMABLE: CargoOffer['status'] = 'APPROVED';

/** Ждущий решения логиста offer — его, как и одобренный, можно отозвать. */
const AWAITING_DECISION: CargoOffer['status'] = 'PENDING';

export function createCargoOffersScreen(options: CargoOffersScreenOptions): CargoOffersScreen {
  const { client, strings, companyId } = options;

  const element = document.createElement('section');
  const heading = document.createElement('h1');
  const notice = document.createElement('p');
  const toAssignLink = document.createElement('a');
  const list = document.createElement('ul');
  const more = document.createElement('button');

  element.className = 'screen screen-cargo-offers';
  heading.textContent = strings.cargo_offers_mine_title;

  notice.className = 'notice';
  notice.setAttribute('role', 'alert');

  toAssignLink.className = 'company-link';
  toAssignLink.href = worksToAssignPath(companyId);
  toAssignLink.textContent = strings.works_to_assign_title;

  more.type = 'button';
  more.className = 'action';
  more.textContent = strings.cargo_offers_more;

  let state: ScreenState | null = 'loading';
  /** Пришёл ли ответ сервера — величина правила места состояния. */
  let answered = false;
  let membership: Membership | null = null;
  let offers: readonly CargoOffer[] = [];
  let cursor: string | null = null;
  /** Действие в пути: кнопки неактивны, второе нажатие запроса не шлёт. */
  let acting = false;
  let loadingMore = false;
  /** Подтверждение удалось — работы рождены, и к ним ведёт ссылка. */
  let confirmed = false;

  function line(text: string): HTMLElement {
    const node = document.createElement('span');

    node.textContent = text;

    return node;
  }

  function button(caption: string, run: () => void): HTMLButtonElement {
    const node = document.createElement('button');

    node.type = 'button';
    node.className = 'action';
    node.textContent = caption;
    node.disabled = acting;
    node.addEventListener('click', run);

    return node;
  }

  function row(offer: CargoOffer): HTMLElement {
    const node = document.createElement('li');
    const locale = activeLocale();
    const parts: string[] = [
      `${offer.origin} → ${offer.destination}`,
      `${strings.cargo_listing_form_loading_at}: ${dateLabel(locale, offer.loading_at)}`,
      offer.cargo_type,
      enumLabel(strings, bodyTypeStringKey(offer.body_type)),
      `${strings.cargo_offers_vehicles}: ${offer.vehicles_count}`,
      `${strings.cargo_offers_price}: ${moneyLabel(strings, offer.price)}`,
      offer.counter ? strings.cargo_offers_counter : strings.cargo_offers_agrees,
      enumLabel(strings, cargoOfferStatusStringKey(offer.status)),
      `${strings.cargo_offers_expires}: ${momentTimeLabel(locale, offer.expires_at)}`,
    ];

    // Срок подтверждения есть только у одобренного offer'а; пустой — не строка.
    // Это показ, а не сравнение с часами: истёк ли срок, отвечает сервер.
    if (offer.confirm_by) {
      parts.push(`${strings.cargo_offers_confirm_by}: ${momentTimeLabel(locale, offer.confirm_by)}`);
    }

    parts.push(`${strings.cargo_offers_sender}: ${offer.sender.name ?? strings.work_assignment_unnamed}`);

    node.className = 'cargo-offers-row';
    node.append(line(parts.join(' · ')));

    // Кнопки — по признаку членства и статусу offer'а; право и срок судит сервер.
    if (membership?.can_take_cargo === true) {
      const approved = offer.status === CONFIRMABLE;

      if (approved) {
        node.append(
          button(strings.cargo_offers_confirm, () => {
            void act(() => client.confirmCargoOffer(offer.id), true);
          }),
        );
      }

      if (approved || offer.status === AWAITING_DECISION) {
        node.append(
          button(strings.cargo_offers_withdraw, () => {
            if (acting) {
              return;
            }

            if (window.confirm(strings.cargo_offers_withdraw_confirm)) {
              void act(() => client.withdrawCargoOffer(offer.id), false);
            }
          }),
        );
      }
    }

    return node;
  }

  function render(): void {
    const rows: HTMLElement[] = [];

    for (const offer of offers) {
      rows.push(row(offer));
    }

    list.replaceChildren(...rows);

    const frame: HTMLElement[] = [heading, notice];

    if (confirmed) {
      frame.push(toAssignLink);
    }

    const after: HTMLElement[] = [];

    if (cursor !== null) {
      after.push(more);
    }

    placeState(element, { state, answered, frame, list, after, emptyCaption: strings.cargo_offers_empty }, strings);
  }

  async function readList(): Promise<void> {
    const page = await client.listCompanyCargoOffers(companyId);

    offers = page.data;
    cursor = nextPageCursor(page.meta);
    answered = true;
    state = screenStateFor({ kind: 'done', empty: offers.length === 0 });
  }

  async function load(): Promise<void> {
    state = screenStateFor({ kind: 'pending' });
    answered = false;
    offers = [];
    cursor = null;
    confirmed = false;
    notice.textContent = '';
    render();

    try {
      const [profile] = await Promise.all([client.getCurrentUser(), readList()]);

      membership = membershipsIn(profile.memberships, companyId)[0] ?? null;
    } catch (error) {
      offers = [];
      cursor = null;
      answered = false;
      state = screenStateFor({
        kind: 'failed',
        code: error instanceof ApiFailure ? error.code : null,
      });
    }

    render();
  }

  /**
   * Действие — подтверждение или отзыв. Только онлайн (решение формы Б17):
   * отказ — текстом сервера над списком, экран остаётся на содержимом. Сервер
   * ответил — успехом или отказом — список перечитывается после его ответа;
   * обрыв связи ответа не дал, и перечитывать нечего.
   */
  async function act(run: () => Promise<unknown>, confirming: boolean): Promise<void> {
    if (acting) {
      return;
    }

    acting = true;
    notice.textContent = '';
    render();

    try {
      try {
        await run();

        if (confirming) {
          confirmed = true;
        }
      } catch (error) {
        notice.textContent = refusalText(error, strings);

        if (!(error instanceof ApiFailure) || error.code === null) {
          return;
        }
      }

      try {
        await readList();
      } catch (error) {
        notice.textContent = refusalText(error, strings);
      }
    } finally {
      acting = false;
      render();
    }
  }

  async function loadMore(): Promise<void> {
    if (cursor === null || loadingMore) {
      return;
    }

    loadingMore = true;
    more.disabled = true;

    try {
      const page = await client.listCompanyCargoOffers(companyId, { cursor });

      // Страница встаёт после прежней в порядке ответа — без отбора и перестановки.
      offers = [...offers, ...page.data];
      cursor = nextPageCursor(page.meta);
    } catch (error) {
      notice.textContent = refusalText(error, strings);
    } finally {
      loadingMore = false;
      more.disabled = false;
    }

    render();
  }

  more.addEventListener('click', () => {
    void loadMore();
  });

  render();

  return {
    element,

    get state(): ScreenState | null {
      return state;
    },

    load,
  };
}
